import { useState, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { mockCapacitySignals } from '@/mocks/capacity';
import { mockOffers } from '@/mocks/offers';
import { mockVerticals } from '@/mocks/verticals';
import { mockGeos } from '@/mocks/geos';
import { Save } from 'lucide-react';

const urgencyBadge: Record<string, { bg: string; color: string; border: string }> = {
  low:    { bg: 'var(--success-bg)',    color: 'var(--success-text)',    border: 'var(--success-border)' },
  medium: { bg: 'var(--warning-bg)',   color: 'var(--warning-text)',   border: 'var(--warning-border)' },
  high:   { bg: 'var(--danger-bg)',    color: 'var(--danger-text)',    border: 'var(--danger-border)' },
};

export default function PartnerCapacityPage() {
  const { user, effectivePartnerId } = useAuth();
  const [signals, setSignals] = useState(mockCapacitySignals);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const partnerSignals = useMemo(() => {
    if (!effectivePartnerId) return [];
    const partnerOffers = mockOffers.filter(o => o.partner_id === effectivePartnerId).map(o => o.id);
    return signals.filter(s => partnerOffers.includes(s.offer_id));
  }, [signals, effectivePartnerId]);

  const updateLoad = (offerId: string, serviceKey: string, value: number) => {
    setSignals(prev => prev.map(s =>
      s.offer_id === offerId && s.service_key === serviceKey
        ? { ...s, current_load: value, updated_at: new Date().toISOString() }
        : s
    ));
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ color: 'var(--text-primary)' }}>Mijn capaciteit</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-tertiary)' }}>Werk je huidige bezetting bij</p>
        </div>
        <button
          onClick={async () => {
            setSaving(true);
            await new Promise(r => setTimeout(r, 600));
            setSaving(false);
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
          }}
          className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-xl transition-all"
          style={{ background: saved ? 'var(--success-bg)' : 'var(--accent)', color: saved ? 'var(--success-text)' : 'var(--text-inverse)' }}
        >
          <Save size={14} />
          {saving ? 'Opslaan...' : saved ? 'Opgeslagen!' : 'Opslaan'}
        </button>
      </div>

      <div className="space-y-3">
        {partnerSignals.map(s => {
          const offer = mockOffers.find(o => o.id === s.offer_id);
          const vertical = mockVerticals.find(v => v.id === offer?.vertical_id);
          const geo = mockGeos.find(g => g.id === offer?.geo_id);
          const badge = urgencyBadge[s.urgency] || urgencyBadge.low;
          const pct = s.max_load ? Math.min((s.current_load / s.max_load) * 100, 100) : 0;
          const barColor = pct >= 100 ? 'var(--danger-text)' : pct >= 70 ? 'var(--warning-text)' : 'var(--success-text)';
          return (
            <div key={`${s.offer_id}-${s.service_key}`} className="hover-card rounded-xl p-5 transition-all duration-300"
              style={{ border: '1px solid' }}
            >
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>{s.display_label}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{vertical?.name} · {geo?.name}</p>
                </div>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium"
                  style={{ background: badge.bg, color: badge.color, border: `1px solid ${badge.border}` }}>
                  {s.urgency}
                </span>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <label className="text-xs" style={{ color: 'var(--text-muted)' }}>Huidige bezetting</label>
                  <input
                    type="number"
                    value={s.current_load}
                    onChange={e => updateLoad(s.offer_id, s.service_key, parseInt(e.target.value) || 0)}
                    className="w-20 px-2 py-1 text-sm rounded-lg text-center transition-all focus:outline-none"
                    style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-secondary)' }}
                  />
                </div>
                <div className="flex-1">
                  <div className="w-full h-[3px] rounded-full overflow-hidden" style={{ background: 'var(--bg-input)' }}>
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${pct}%`, background: barColor }}
                    />
                  </div>
                </div>
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  Max: {s.max_load || '∞'}
                </span>
              </div>
              <div className="mt-3">
                <input
                  type="text"
                  defaultValue={s.note || ''}
                  placeholder="Notitie toevoegen..."
                  className="w-full text-xs rounded-lg px-2.5 py-1.5 focus:outline-none transition-all"
                  style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-tertiary)' }}
                />
              </div>
            </div>
          );
        })}
        {partnerSignals.length === 0 && (
          <div className="text-center py-8 rounded-xl" style={{ background: 'var(--bg-surface-raised)', border: '1px solid var(--border-primary)' }}>
            <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>Geen capaciteitssignalen ingesteld voor jouw aanbod</p>
          </div>
        )}
      </div>
    </div>
  );
}